import React, { useEffect, useMemo, useRef, useState } from "react"
import { MdRadioButtonChecked, MdRadioButtonUnchecked } from "react-icons/md"
import { AnnotationGroup } from "../../../Types/Annotations"
import { useDataReviewPermissions } from "../../../../../../../Hooks/useDataReviewPermissions"

export type SessionSelectorChangeEvent = {
	type: "create" | "existing"
	sessionId: string
	sessionName: string
}

type DefaultAnnotationSessionSelectorProps = {
	annotationSessions: AnnotationGroup[]
	onChange?: (event: SessionSelectorChangeEvent) => void
	validationError?: string
}

export type SessionOption = {
	type: "create" | "existing"
	label: string
}

const options: SessionOption[] = [
	{
		type: "existing",
		label: "Continue with an existing session",
	},
	{
		type: "create",
		label: "Create a new session",
	},
]

export const DefaultAnnotationSessionSelector = (props: DefaultAnnotationSessionSelectorProps) => {
	const { annotationSessions, onChange, validationError } = props
	const writableAnnotationSessions = useMemo(() => annotationSessions.filter(session => !session.read_only), [annotationSessions])
	const [selectedOption, setSelectedOption] = useState(options[0])
	const [newSessionName, setNewSessionName] = useState("")
	const [selectedAnnotationSessionId, setSelectedAnnotationSessionId] = useState<string | undefined>(writableAnnotationSessions.length > 0 ? writableAnnotationSessions[0].id.toString() : undefined)
	const selectedSessionExists = writableAnnotationSessions.some(session => session.id.toString() === selectedAnnotationSessionId)
	const effectiveSelectedAnnotationSessionId = selectedSessionExists ? selectedAnnotationSessionId : writableAnnotationSessions[0]?.id.toString()

	const previousEvent = useRef<SessionSelectorChangeEvent>()
	const { canManageDataReview } = useDataReviewPermissions()

	useEffect(() => {
		let event: SessionSelectorChangeEvent = {
			type: selectedOption.type,
			sessionId: "",
			sessionName: "",
		}

		switch (selectedOption.type) {
			case "create":
				event.sessionName = newSessionName.trim()
				break
			case "existing":
				if (effectiveSelectedAnnotationSessionId) {
					event.sessionId = effectiveSelectedAnnotationSessionId
				}
				break
		}

		const changed = JSON.stringify(previousEvent.current) !== JSON.stringify(event)

		if (changed && onChange) {
			onChange(event)
			previousEvent.current = event
		}
	}, [effectiveSelectedAnnotationSessionId, newSessionName, onChange, selectedOption])

	return (
		<div>
			<div>
				{options.map(option => {
					const optionDisabled = option.type === 'create' && !canManageDataReview
					return (
						<div key={option.type} title={optionDisabled ? 'You do not have permission for this action' : ''}>
							<button
								style={{ display: "flex", padding: "5px 0", gap: "8px", fontSize: "16px", border: "none", background: "none", color: "var(--color-text-primary)", cursor: optionDisabled ? 'not-allowed' : 'pointer', alignItems: 'center' }}
								onMouseDown={() => setSelectedOption(option)}
								id={`${option.type}-${!canManageDataReview}`}
								disabled={optionDisabled}
							>
								<div style={{ opacity: optionDisabled ? 0.2 : 1, display: 'flex' }}>{option.label === selectedOption.label ? <MdRadioButtonChecked size={20} color={"var(--color-accent-interactive)"} /> : <MdRadioButtonUnchecked size={20} />}</div>

								<div> 
									<p>
										{option.label}
									</p>
								</div>
							</button>
						</div>
					)
				})}
			</div>

			<div style={{ padding: "30px 0", width: "100%" }}>
				<div style={{ fontWeight: "bold", paddingBottom: "10px" }}> New annotations will be written to: </div>

				{selectedOption.type === "create" && (
					<div>
						<input
							type="text"
							value={newSessionName}
							onChange={event => setNewSessionName(event.target.value)}
							style={{ width: "100%", padding: "10px", border: "1px solid var(--color-border-default)", borderRadius: "6px", height: '41px', backgroundColor: "var(--color-input-bg)", color: "var(--color-text-primary)" }}
						/>
						<div style={{ color: "var(--color-danger-text)" }}> {validationError ? validationError : ""} </div>
					</div>
				)}

				{selectedOption.type === "existing" && (
					<div>
						<select
							value={effectiveSelectedAnnotationSessionId}
							onChange={event => setSelectedAnnotationSessionId(event.target.value)}
							style={{ width: "100%", padding: "10px", border: "1px solid var(--color-border-default)", borderRadius: "6px", cursor: "pointer", height: '41px', backgroundColor: "var(--color-input-bg)", color: "var(--color-text-primary)" }}
						>
							{writableAnnotationSessions.map(session => {
								return (
									<option key={session.id} value={session.id}>
										{session.group_name}
									</option>
								)
							})}
						</select>
					</div>
				)}
			</div>
		</div>
	)
}
